/**
 * ════════════════════════════════════════════════════════════════════════════
 * React Error Boundary
 * ════════════════════════════════════════════════════════════════════════════
 * Дата: 24.01.2026
 * Назначение: Перехват ошибок рендеринга React компонентов
 * 
 * Использование:
 *   <ErrorBoundary fallback={<ErrorFallback />}>
 *     <App />
 *   </ErrorBoundary>
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/utils/logger';

// ════════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════════

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

// ════════════════════════════════════════════════════════════════════════════
// Error Boundary Component
// ════════════════════════════════════════════════════════════════════════════

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Логируем ошибку через наш логгер
    logger.logReactError(error, errorInfo.componentStack || '');

    // Сохраняем для отображения
    this.setState({ errorInfo });

    // Callback если передан
    this.props.onError?.(error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Кастомный fallback если передан
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Дефолтный fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            {/* Иконка ошибки */}
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>

            {/* Заголовок */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Что-то пошло не так
            </h2>

            {/* Описание */}
            <p className="text-gray-600 mb-6">
              Произошла непредвиденная ошибка. Мы уже уведомлены и работаем над исправлением.
            </p>

            {/* Детали ошибки в dev режиме */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-3 bg-red-50 rounded text-left text-sm">
                <p className="font-mono text-red-800 break-all">
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-600 hover:text-red-800">
                      Component Stack
                    </summary>
                    <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Кнопки */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                На главную
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Перезагрузить
              </button>
            </div>

            {/* Session ID для отладки */}
            <p className="mt-6 text-xs text-gray-400">
              Session: {logger.getSessionId()}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Minimal Error Fallback (для вложенных границ)
// ════════════════════════════════════════════════════════════════════════════

export function MinimalErrorFallback(): JSX.Element {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p className="text-red-800 text-sm">
        Ошибка загрузки компонента
      </p>
      <button 
        onClick={() => window.location.reload()}
        className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
      >
        Перезагрузить страницу
      </button>
    </div>
  );
}

export default ErrorBoundary;
